import { setBlockType } from 'prosemirror-commands';
import { EditorState, Transaction, Plugin, PluginKey, Command } from 'prosemirror-state';
import { isMarkActive, isNodeActive } from 'ngx-editor/helpers';
import { EditorView } from 'prosemirror-view';
import { DOMOutputSpec, Mark, MarkSpec, Schema } from 'prosemirror-model';
import { ArticleBlock } from '../../../../models/article';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, HostListener, Renderer2, OnChanges } from '@angular/core';
import { Editor, Toolbar, marks as basicMarks, nodes } from 'ngx-editor';
import { toggleMark } from 'prosemirror-commands';
import { removeLink } from 'ngx-editor/commands';
import { Dispatch } from 'ngx-editor/lib/commands/types';
import type { MarkType } from 'prosemirror-model';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
const defaultOptions = {
  strict: false,
};


export interface LinkAttrs {
  selectedText?: string,
  tdata?: string
}

@Component({
  selector: 'app-article-paragraph',
  templateUrl: './article-paragraph.component.html',
  styleUrls: ['./article-paragraph.component.scss']
})
export class ArticleParagraphComponent implements OnInit, OnChanges, OnDestroy {

  @Output() blockElementChange = new EventEmitter<ArticleBlock>();

  @Input() blockElement: ArticleBlock;

  editor: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['align_left', 'align_center', 'align_right', 'align_justify'], ['link']
  ];

  tooltipMark: MarkSpec = {
    attrs: {
      tdata: {},
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'span[tdata]',
        getAttrs(dom: HTMLElement): Record<string, any> {
          return {
            tdata: dom.getAttribute('tdata'),
          };
        }
      }],
    toDOM(node): DOMOutputSpec {
      const { tdata } = node.attrs;
      return ['span', { tdata, class: "block-tooltip" }, 0];
    }
  };

  marks = Object.assign({}, basicMarks, {
    tooltip: this.tooltipMark,
  });

  schema = new Schema({
    nodes,
    marks: this.marks,
  });


  onClick(e: any) {
    if (e.button !== 0) {
      return;
    }

    const { state, dispatch } = this.editor.view;

    if (this.isActiveBtn) {
      this.removeTooltip(state, dispatch);
      return;
    }

    this.showPopup = !this.showPopup;
    if (this.showPopup) {
      this.setText();
    }
  }

  private setText = () => {
    const { state: { selection, doc } } = this.editor.view;
    const { empty, from, to } = selection;
    this.selectedText = !empty ? doc.textBetween(from, to) : '';
  }

  constructor(private el: ElementRef, private sanitizer: DomSanitizer, private renderer: Renderer2) { }


  ngOnInit(): void {
    this.editor = new Editor({
      schema: this.schema,
    });

    const plugin = new Plugin({
      key: new PluginKey(`custom-menu-tooltip`),
      view: () => {
        return {
          update: this.detectChanges,
        };
      },
    });

    this.editor.registerPlugin(plugin);

  }

  ngOnChanges(): void {
    setTimeout(() => {
      if (this.blockElement?.isActiveForEdit) {
        const editorElement = this.el.nativeElement.querySelector('.ProseMirror.NgxEditor__Content');
        this.renderer.setAttribute(editorElement, 'autofocus', 'true');
        editorElement.focus();
      }
    }, 700);
  }

  getSanitizedHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }


  detectChanges = (view: EditorView) => {
    const { state } = view;
    const { schema } = state;
    this.isActiveBtn = this.isActiveTooltip(state);
    this.canExecuteBtn = this.canExecute(state);
  };

  onChange(e: string) {
    this.blockElement.data = e;
    this.blockElementChange.emit(this.blockElement);
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  // @HostListener('document:mousedown', ['$event']) onDocumentClick(e: MouseEvent): void {
  //   let a = this.el.nativeElement.querySelector('.tooltipModel');
  //   if (!a.contains(e.target) && this.showPopup) {
  //     this.showPopup = false;
  //     this.selectedText = '';
  //     this.titleValue = '';
  //   }
  // }

  titleValue: string;
  showPopup: boolean = false;
  selectedText: string;
  isActiveBtn: boolean = false;
  canExecuteBtn: boolean = true;

  addTitle(e: any) {
    e.preventDefault();
    const text = this.titleValue;
    const selectedText = this.selectedText;
    const { state, dispatch } = this.editor.view;
    const { selection } = state;
    this.isActiveBtn = this.isActiveTooltip(state);
    this.canExecuteBtn = this.canExecute(state);
    const attrs = {
      selectedText: selectedText,
      tdata: text
    };

    if (selection.empty) {
      this.insertTooltip(selectedText, attrs)(state, dispatch);
      this.editor.view.focus();
    } else {
      this.updateToolTip(attrs)(state, dispatch);
    }
    this.showPopup = false;
    this.selectedText = '';
    this.titleValue = '';
  }

  updateToolTip(attrs: LinkAttrs): Command {
    return (state: EditorState, dispatch?: Dispatch): boolean => {
      const { schema, selection } = state;

      const type: MarkType = schema.marks['tooltip'];
      if (!type) {
        return false;
      }

      if (selection.empty) {
        return false;
      }

      return toggleMark(type, attrs)(state, dispatch);
    };
  }

  insertTooltip(text: string, attrs: LinkAttrs): Command {
    return (state: EditorState, dispatch?: Dispatch): boolean => {
      const { schema, tr } = state;
      const type: MarkType = schema.marks['tooltip'];
      if (!type) {
        return false;
      }

      const linkAttrs: LinkAttrs = {
        tdata: attrs.tdata ?? text,
      };

      const node = schema.text(text, [schema.marks['tooltip'].create(linkAttrs)]);

      tr.replaceSelectionWith(node, false)
        .scrollIntoView();

      if (tr.docChanged) {
        dispatch?.(tr);
        return true;
      }

      return false;
    };
  }

  isActiveTooltip(state: EditorState, options = defaultOptions): boolean {
    if (options.strict) {
      return true;
    }

    const { schema } = state;
    const type = schema.marks['tooltip'];

    if (!type) {
      return false;
    }

    return isMarkActive(state, type);
  }

  removeTooltip(state: EditorState, dispatch?: Dispatch): boolean {
    return removeLink()(state, dispatch);
  }

  canExecute(state: EditorState): boolean {
    const testAttrs: LinkAttrs = {
      tdata: '',
    };

    return this.insertTooltip('Exec', testAttrs)(state) || this.updateToolTip(testAttrs)(state);
  }
}
